import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  Inject,
  Injectable,
  LOCALE_ID,
  NgZone,
  PLATFORM_ID,
} from '@angular/core';
import { Auth } from '@freelancer/auth';
import {
  AppVersion,
  FreelancerPwaTrackingInterface,
  NativeDeviceInfo,
  Platform,
  Pwa,
} from '@freelancer/pwa';
import { leaveZone, Timer, TimeUtils } from '@freelancer/time-utils';
import { CookieService } from '@laurentgoudet/ngx-cookie';
import * as Rx from 'rxjs';
import {
  buffer,
  debounceTime,
  map,
  mapTo,
  observeOn,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { FacebookPixelTracking } from './facebook-pixel-tracking.service';
import { GoogleTracking } from './google-tracking.service';
import { TRACKING_CONFIG } from './tracking.config';
import { TrackingConfig } from './tracking.interface';
import {
  CustomTrackingEvent,
  HeartbeatTrackingCancellation,
  TrackingEvent,
  TrackingEventData,
  TrackingEventType,
} from './tracking.model';

export interface TrackingBeacon {
  beaconId: string;
  payload: TrackingEvent & TrackingEventData;
}

@Injectable()
export class Tracking implements FreelancerPwaTrackingInterface {
  private sessionId: string;
  /** Unique to each tab, to enable identifying individual tabs for users. */
  private clientId: string;
  private referrerUrl: string;
  private platform: Platform;
  private isNative: boolean;
  private isInstalled: boolean;
  private appVersion?: AppVersion;
  private nativeDeviceInfo?: NativeDeviceInfo;
  private eventsSubject$ = new Rx.Subject<TrackingBeacon>();
  private events$: Rx.Observable<TrackingBeacon> = this.eventsSubject$.asObservable();
  private initPromise?: Promise<void>;

  constructor(
    private auth: Auth,
    private cookies: CookieService,
    private facebookPixelTracking: FacebookPixelTracking,
    private googleTracking: GoogleTracking,
    private http: HttpClient,
    private ngZone: NgZone,
    private timeUtils: TimeUtils,
    private pwa: Pwa,
    @Inject(TRACKING_CONFIG) private config: TrackingConfig,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(LOCALE_ID) private locale: string,
  ) {}

  // DO NOT CALL THAT FROM THE app/ CODE. IT'LL BE RESTRICTED SOON.
  async track(
    eventType: TrackingEventType,
    eventData: TrackingEventData = {},
  ): Promise<void> {
    await this.init();

    return this.auth.authState$
      .pipe(take(1))
      .toPromise()
      .then(auth => {
        const e: TrackingEvent = {
          ...{
            en: eventType,
            acct: window.location.hostname.replace(/^www/, ''),
            platform: this.platform,
            isNative: this.isNative,
            isInstalled: this.isInstalled,
            appVersion: this.appVersion,
            nativeDeviceInfo: this.nativeDeviceInfo,
            t: Date.now().toString(),
            location: window.location.href,
            user_id: auth ? parseInt(auth.userId, 10) : undefined,
            session_id: this.sessionId,
          },
          ...eventData,
        };
        return this.sendBeacon(e);
      });
  }

  getSessionId(): Rx.Observable<string> {
    // sessionId could eventually be turned into a true observable
    return Rx.of(undefined).pipe(
      switchMap(async () => {
        await this.init();
      }),
      map(() => this.sessionId),
    );
  }

  async trackPageView(is404?: boolean): Promise<void> {
    await this.init();

    this.track('page_view', {
      language: this.locale,
      referrer_url: this.referrerUrl,
      screenHeight: window.screen.height,
      screenWidth: window.screen.width,
      windowInnerHeight: window.innerHeight,
      windowInnerWidth: window.innerWidth,
      is_404: is404,
    });
    // update the referrerUrl so that the next page_view event contains it.
    this.referrerUrl = window.location.href;
    // if GA tracking is enabled, track the page view there as well
    if (this.config.gaTrackingId) {
      this.googleTracking.trackPageView();
    }
    // if the FB pixel is enabled, track the page view there as well
    if (this.config.facebookPixelId) {
      this.facebookPixelTracking.trackPageView();
    }
  }

  async trackCustomEvent(
    name: string,
    section?: string,
    extraParams?: { [key: string]: string | number | string[] },
  ): Promise<void> {
    await this.init();

    this.track('custom_event', {
      name,
      section,
      extra_params: {
        ...extraParams,
        client_id: this.clientId,
      },
    });
  }

  async trackHeartbeat(
    name: string,
    referenceType?: string,
    referenceId?: string,
  ): Promise<HeartbeatTrackingCancellation> {
    await this.init();

    const startTime = Date.now();
    let count = 0;
    let interval = 1000;
    let timeoutId: Timer;

    const track = () => {
      count += 1;
      this.track('heart_beat', {
        name,
        start_time: startTime,
        count,
        reference: referenceType,
        reference_id: referenceId,
      });
      interval = Math.round(interval * 1.15);
      timeoutId = this.timeUtils.setTimeout(() => track(), interval);
    };

    track();

    return () => {
      clearTimeout(timeoutId);
    };
  }

  // Only to be called by the Tracking component
  async sendEventBacklog(): Promise<void> {
    await this.init();

    // send the event backlog if any
    try {
      const backlog = localStorage.getItem('trackingBacklog');
      if (backlog) {
        const itemsMap: { [items: string]: TrackingEvent } = JSON.parse(
          backlog,
        );
        Object.entries(itemsMap).forEach(([beaconId, payload]) => {
          this.eventsSubject$.next({
            beaconId,
            payload,
          });
        });
      }
      localStorage.setItem('trackingBacklog', JSON.stringify({}));
    } catch (e) {
      // ignore the errors, e.g. quota is full or security error
      console.error(e);
    }
  }

  private async init(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      throw new Error('The tracking service cannot be used on the server-side');
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = Promise.resolve().then(async () => {
      let sessionId = this.cookies.get(this.config.sessionCookie);
      if (!sessionId) {
        // Generate a new one if not.
        sessionId = this.generateUuid();
        this.cookies.put('_tracking_session', sessionId, {
          expires: new Date(
            new Date().setFullYear(new Date().getFullYear() + 1),
          ),
        });
      }
      this.sessionId = sessionId;

      let clientId: string | null | undefined;
      try {
        clientId = window.sessionStorage.getItem(this.config.clientKey);
      } catch (e) {
        // ignore the errors, e.g. quota is full or security error
        console.error(e);
      }
      if (!clientId) {
        clientId = this.generateUuid();
        try {
          window.sessionStorage.setItem(this.config.clientKey, clientId);
        } catch (e) {
          // ignore the errors, e.g. quota is full or security error
          console.error(e);
        }
      }
      this.clientId = clientId;

      // set the external referrer on start
      this.referrerUrl = document.referrer;

      // the mode the app runs in
      this.platform = this.pwa.getPlatform();
      this.isNative = this.pwa.isNative();
      this.isInstalled = this.pwa.isInstalled();
      this.appVersion = await this.pwa.appVersion().pipe(take(1)).toPromise();

      if (this.pwa.isNative()) {
        this.nativeDeviceInfo = await this.pwa
          .nativeDeviceInfo()
          .pipe(take(1))
          .toPromise();
      }

      this.events$
        .pipe(
          observeOn(leaveZone(this.ngZone, Rx.asyncScheduler)),
          // buffer the events for a 1s rolling window to limit the number of
          // requests
          // FIXME: decreased to 100ms to test if it fixes T229388
          buffer(
            this.events$.pipe(
              debounceTime(100, leaveZone(this.ngZone, Rx.asyncScheduler)),
            ),
          ),
          switchMap(beacons =>
            'sendBeacon' in navigator &&
            // sendBeacon() returns false if the user agent fails to queue the
            // data for transfer (e.g. quota reached), falling back to
            // http.post() here.
            navigator.sendBeacon(
              this.config.trackingEndpoint,
              new Blob([JSON.stringify(beacons.map(b => b.payload))], {
                type: 'application/json',
              }),
            )
              ? Rx.of(beacons.map(b => b.beaconId))
              : this.http
                  .post(
                    this.config.trackingEndpoint,
                    beacons.map(b => b.payload),
                  )
                  .pipe(mapTo(beacons.map(b => b.beaconId))),
          ),
          tap(beaconIds => {
            // Remove the event from the backlog once it has been sent
            try {
              const backlog = JSON.parse(
                (localStorage.getItem('trackingBacklog') as string) || '{}',
              );
              beaconIds.forEach(beaconId => {
                delete backlog[beaconId];
              });
              localStorage.setItem('trackingBacklog', JSON.stringify(backlog));
            } catch (e) {
              // ignore the errors, e.g. quota is full or security error
              console.error(e);
            }
          }),
          mapTo(undefined),
        )
        .toPromise()
        .catch((err: HttpErrorResponse) => {
          if (err.error instanceof Error) {
            // A client-side or network error occurred.
            throw err.error;
          } else if (err.status === 0) {
            // HTTP status 0 means that the request was blocked by the client
            // (browser), which is often due to an Ad blocker.
            console.error(err.message);
          } else {
            // The backend returned an unsuccessful response code
            throw err.message;
          }
        });
    });
    return this.initPromise;
  }

  private generateUuid(): string {
    const s4 = () =>
      Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1);
    return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
  }

  private sendBeacon(payload: TrackingEvent & TrackingEventData): void {
    const beaconId = Date.now().toString();

    // save the event for later in case of a hard navigation
    try {
      const backlog = JSON.parse(
        (localStorage.getItem('trackingBacklog') as string) || '{}',
      );
      backlog[beaconId] = payload;
      localStorage.setItem('trackingBacklog', JSON.stringify(backlog));
    } catch (e) {
      // ignore the errors, e.g. quota is full or security error
      console.error(e);
    }

    // if GA tracking is enabled, track the click event there
    if (this.config.gaTrackingId && payload.action === 'click') {
      this.googleTracking.trackClickEvent(payload);
    }

    // Forward custom event to Google and Facebook tracking.
    // Custom event name is required for a custom event.
    if (this.isCustomTrackingEvent(payload)) {
      if (this.config.gaTrackingId) {
        this.googleTracking.trackCustomEvent(payload);
      }
      if (this.config.facebookPixelId) {
        this.facebookPixelTracking.trackCustomEvent(payload);
      }
    }

    this.eventsSubject$.next({
      beaconId,
      payload,
    });
  }

  // Type guard for custom event
  private isCustomTrackingEvent(
    event: TrackingEvent & TrackingEventData,
  ): event is CustomTrackingEvent & TrackingEventData {
    return (
      (event as CustomTrackingEvent & TrackingEventData).en === 'custom_event'
    );
  }
}
